import { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { createTheme, Theme, getContrastRatio } from '@mui/material/styles';
import { designTokens } from '../theme/designTokens';
import apiClient from '../services/api/config';

// Pick readable text for any branded background colour. The default primary
// (#2D5BE3 lime) and many uploaded brand colours are too light for white
// text — without this the "Create" / "Save" buttons render white-on-lime and
// disappear. WCAG AA large-text is 3:1; we aim higher (4.5:1) by trying
// white first and falling back to ink (#0F172A) when white fails.
function readableContrastText(bg: string): string {
  const ink = designTokens.colors.text.primary || '#0F172A';
  try {
    return getContrastRatio(bg, '#FFFFFF') >= 4.5 ? '#FFFFFF' : ink;
  } catch {
    return ink;
  }
}

interface CompanyBranding {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  sidebarColor: string;
  sidebarTextColor: string;
  headerColor: string;
  headerTextColor: string;
  logoUrl: string;
  companyName: string;
  airaIconUrl: string;
}

interface BrandingContextType {
  branding: CompanyBranding;
  theme: Theme;
  refreshBranding: () => Promise<void>;
  mode: ThemeMode;
  toggleMode: () => void;
}

// Day/night register. Persisted per-browser; first visit honours the OS.
const MODE_KEY = 'mm-theme-mode';
function initialMode(): ThemeMode {
  try {
    const saved = localStorage.getItem(MODE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
    if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) return 'dark';
  } catch {}
  return 'light';
}

const defaultBranding: CompanyBranding = {
  primaryColor: '#2D5BE3',
  secondaryColor: '#8B5CF6',
  accentColor: '#F59E0B',
  sidebarColor: '#1A1D1F',
  sidebarTextColor: '#FFFFFF',
  headerColor: '#FFFFFF',
  headerTextColor: '#1A1D1F',
  logoUrl: '',
  companyName: '',
  airaIconUrl: '',
};

export type ThemeMode = 'light' | 'dark';

function buildTheme(b: CompanyBranding, mode: ThemeMode = 'light'): Theme {
  const primaryMain = b.primaryColor || defaultBranding.primaryColor;
  const secondaryMain = b.secondaryColor || defaultBranding.secondaryColor;
  const dark = mode === 'dark';
  // Mode-scoped ground/text/border. Brand primary/secondary stay per-tenant.
  const bg = dark ? designTokens.colors.backgroundDark : designTokens.colors.background;
  const txt = dark ? designTokens.colors.textDark : designTokens.colors.text;
  const brd = dark ? designTokens.colors.borderDark : designTokens.colors.border;
  return createTheme({
    palette: {
      mode,
      primary: {
        main: primaryMain,
        light: designTokens.colors.primary[300],
        dark: designTokens.colors.primary[700],
        contrastText: readableContrastText(primaryMain),
      },
      secondary: {
        main: secondaryMain,
        light: designTokens.colors.secondary[300],
        dark: designTokens.colors.secondary[700],
        contrastText: readableContrastText(secondaryMain),
      },
      error: {
        main: designTokens.colors.error.main,
        light: designTokens.colors.error.light,
        dark: designTokens.colors.error.dark,
      },
      warning: {
        main: designTokens.colors.warning.main,
        light: designTokens.colors.warning.light,
        dark: designTokens.colors.warning.dark,
      },
      info: {
        main: designTokens.colors.info.main,
        light: designTokens.colors.info.light,
        dark: designTokens.colors.info.dark,
      },
      success: {
        main: designTokens.colors.success.main,
        light: designTokens.colors.success.light,
        dark: designTokens.colors.success.dark,
      },
      background: {
        default: bg.default,
        paper: bg.paper,
      },
      text: {
        primary: txt.primary,
        secondary: txt.secondary,
        disabled: txt.disabled,
      },
      divider: brd.main,
    },
    typography: {
      fontFamily: designTokens.typography.fontFamily.primary,
      h1: { fontSize: designTokens.typography.fontSize['5xl'], fontWeight: designTokens.typography.fontWeight.bold, lineHeight: designTokens.typography.lineHeight.tight, letterSpacing: designTokens.typography.letterSpacing.tight },
      h2: { fontSize: designTokens.typography.fontSize['4xl'], fontWeight: designTokens.typography.fontWeight.bold, lineHeight: designTokens.typography.lineHeight.tight, letterSpacing: designTokens.typography.letterSpacing.tight },
      h3: { fontSize: designTokens.typography.fontSize['3xl'], fontWeight: designTokens.typography.fontWeight.semibold, lineHeight: designTokens.typography.lineHeight.tight, letterSpacing: designTokens.typography.letterSpacing.tight },
      h4: { fontSize: designTokens.typography.fontSize['2xl'], fontWeight: designTokens.typography.fontWeight.semibold, lineHeight: designTokens.typography.lineHeight.normal, letterSpacing: designTokens.typography.letterSpacing.tight },
      h5: { fontSize: designTokens.typography.fontSize.xl, fontWeight: designTokens.typography.fontWeight.semibold, lineHeight: designTokens.typography.lineHeight.normal },
      h6: { fontSize: designTokens.typography.fontSize.lg, fontWeight: designTokens.typography.fontWeight.semibold, lineHeight: designTokens.typography.lineHeight.normal },
      body1: { fontSize: designTokens.typography.fontSize.base, lineHeight: designTokens.typography.lineHeight.normal, fontWeight: designTokens.typography.fontWeight.regular },
      body2: { fontSize: designTokens.typography.fontSize.sm, lineHeight: designTokens.typography.lineHeight.normal, fontWeight: designTokens.typography.fontWeight.regular },
    },
    spacing: 8,
    shape: { borderRadius: 12 },
    // Graded elevation ramp — steps sm→base→md→lg→xl→2xl instead of jumping to
    // the heavy 2xl at elevation 6 (which flattened every menu/dialog/popover).
    shadows: [
      'none',
      designTokens.shadows.sm, designTokens.shadows.sm, designTokens.shadows.base,
      designTokens.shadows.base, designTokens.shadows.md, designTokens.shadows.md,
      designTokens.shadows.md, designTokens.shadows.lg, designTokens.shadows.lg,
      designTokens.shadows.lg, designTokens.shadows.lg, designTokens.shadows.xl,
      designTokens.shadows.xl, designTokens.shadows.xl, designTokens.shadows.xl,
      designTokens.shadows['2xl'], designTokens.shadows['2xl'], designTokens.shadows['2xl'],
      designTokens.shadows['2xl'], designTokens.shadows['2xl'], designTokens.shadows['2xl'],
      designTokens.shadows['2xl'], designTokens.shadows['2xl'], designTokens.shadows['2xl'],
    ],
    components: {
      MuiBackdrop: {
        styleOverrides: {
          root: { backgroundColor: 'rgba(30, 41, 59, 0.5)', backdropFilter: 'blur(2px)' },
          invisible: { backgroundColor: 'transparent', backdropFilter: 'none' },
        },
      },
      // ── Containers (Card / Paper / Dialog) all use the same 24px radius ──
      // Matches the dashboard card style (sx={{ borderRadius: '24px' }})
      // that the various module pages apply manually. Centralising it here
      // means every Paper/Card/Dialog inherits the same look without
      // per-page sx props.
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: designTokens.shadows.base,
            borderRadius: 16,
            border: `1px solid ${brd.light}`,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            // Don't force radius on Snackbar/Menu/Tooltip Papers — those
            // look weird at 24px. They explicitly set elevation === 0 or
            // small radius themselves.
            backgroundImage: 'none',
          },
          elevation1: { borderRadius: 16 },
          elevation2: { borderRadius: 16 },
          elevation3: { borderRadius: 20 },
          elevation4: { borderRadius: 20 },
          rounded: { borderRadius: 16 },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: 20 },
        },
      },
      // ── Tabs: one branded treatment for every tab bar in the app ──
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 44 },
          indicator: {
            height: 3,
            borderRadius: '3px 3px 0 0',
            backgroundColor: primaryMain,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            minHeight: 44,
            color: txt.secondary,
            transition: 'color 0.15s ease',
            '&.Mui-selected': { color: primaryMain, fontWeight: 700 },
            '&:hover': { color: txt.primary },
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            '&:before': { display: 'none' },
            '&.Mui-expanded': { margin: 'auto' },
          },
        },
      },

      // ── Form inputs all use 16px radius for a softer, modern look ──
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': { borderRadius: 16 },
            '& .MuiOutlinedInput-root.MuiInputBase-multiline': { borderRadius: 16 },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 16 },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            borderRadius: '16px 16px 0 0',
            '&:before, &:after': { display: 'none' },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: { borderRadius: 16 },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            // Catch any Inputs that aren't TextField-wrapped
            '&.MuiOutlinedInput-root': { borderRadius: 16 },
          },
        },
      },

      // ── Action elements — rounded but not pills ──
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none' as const,
            fontWeight: designTokens.typography.fontWeight.semibold,
            borderRadius: 14,
            padding: `${designTokens.spacing[2]} ${designTokens.spacing[6]}`,
            transition: designTokens.transitions.base,
          },
          sizeLarge: {
            padding: `${designTokens.spacing[3]} ${designTokens.spacing[8]}`,
            fontSize: designTokens.typography.fontSize.lg,
            borderRadius: 16,
          },
          sizeSmall: { borderRadius: 12 },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: { borderRadius: 14, textTransform: 'none' as const },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 999 }, // pill — Chip's natural style
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 16 },
        },
      },
      MuiSnackbarContent: {
        styleOverrides: {
          root: { borderRadius: 14 },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          rounded: { borderRadius: 16 },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: { borderRadius: 16 },
        },
      },
    },
  });
}

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<CompanyBranding>(defaultBranding);
  const [mode, setMode] = useState<ThemeMode>(initialMode);

  const toggleMode = () => {
    setMode(prev => {
      const next = prev === 'light' ? 'dark' : 'light';
      try { localStorage.setItem(MODE_KEY, next); } catch {}
      document.documentElement.setAttribute('data-theme', next);
      return next;
    });
  };

  const applyCSS = (b: CompanyBranding) => {
    const root = document.documentElement;
    root.style.setProperty('--brand-primary', b.primaryColor);
    root.style.setProperty('--brand-secondary', b.secondaryColor);
    root.style.setProperty('--brand-accent', b.accentColor);
    root.style.setProperty('--brand-sidebar', b.sidebarColor);
    root.style.setProperty('--brand-sidebar-text', b.sidebarTextColor);
    root.style.setProperty('--brand-header', b.headerColor);
    root.style.setProperty('--brand-header-text', b.headerTextColor);
    // Keep the PWA/browser chrome color in sync with the company's brand.
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', b.primaryColor);
  };

  const loadBranding = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const response = await apiClient.get('/company/branding');
      if (response.data?.success && response.data?.data) {
        const d = response.data.data;
        const newBranding = {
          primaryColor: d.primaryColor || defaultBranding.primaryColor,
          secondaryColor: d.secondaryColor || defaultBranding.secondaryColor,
          accentColor: d.accentColor || defaultBranding.accentColor,
          sidebarColor: d.sidebarColor || defaultBranding.sidebarColor,
          sidebarTextColor: d.sidebarTextColor || defaultBranding.sidebarTextColor,
          headerColor: d.headerColor || defaultBranding.headerColor,
          headerTextColor: d.headerTextColor || defaultBranding.headerTextColor,
          logoUrl: d.logoUrl || '',
          companyName: d.companyName || '',
          airaIconUrl: d.airaIconUrl || '',
        };
        setBranding(newBranding);
        applyCSS(newBranding);
      }
    } catch (err) {
      // Use defaults
    }
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
    applyCSS(defaultBranding);
    loadBranding();
    const handleStorage = () => loadBranding();
    const handleLogin = () => loadBranding();
    window.addEventListener('storage', handleStorage);
    window.addEventListener('auth-login', handleLogin);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('auth-login', handleLogin);
    };
  }, []);

  const theme = useMemo(() => buildTheme(branding, mode), [branding, mode]);

  const value = useMemo(() => ({
    branding,
    theme,
    refreshBranding: loadBranding,
    mode,
    toggleMode,
  }), [branding, theme, mode]);

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  const context = useContext(BrandingContext);
  if (!context) {
    return {
      branding: defaultBranding,
      theme: buildTheme(defaultBranding),
      refreshBranding: async () => {},
      mode: 'light' as ThemeMode,
      toggleMode: () => {},
    };
  }
  return context;
}
